import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  doc,
  getDoc,
} from 'firebase/firestore';
import { db } from '../../utils/firebase'; // Adjust path to your Firebase config
import { FaArrowLeft, FaTrophy, FaChevronDown, FaCheckCircle } from 'react-icons/fa';
import {useLang} from "../../context/LanguageContext";
import Footer from "../../components/Footer";

const WinningTicketsPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [tickets, setTickets] = useState([]);           // All winning tickets
  const [expandedTicket, setExpandedTicket] = useState(null); // ID of ticket whose dropdown is open
  const [gamesMap, setGamesMap] = useState({});         // { ticketId: [gameObjects] }
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // 1) Fetch winning tickets on mount
    const fetchWinningTickets = async () => {
      try {
        setLoadingTickets(true);
        setError(null);

        // Firestore query
        const colRef = collection(db, 'generated');
        const q = query(
          colRef,
          where('status', '==', 'win'),
          orderBy('dateCreated', 'desc'),
          limit(1000)
        );

        const snapshot = await getDocs(q);
        const results = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Condition: totalOdd > 5.0
          if (data.totalOdd > 10.0) {
            // You can add a date filter if needed (like your validateDate logic)
            results.push({
              id: docSnap.id,
              dateCreated: data.dateCreated,
              meciuri: data.meciuri, // array of doc references or doc IDs
              totalOdd: data.totalOdd,
            });
          }
        });

        setTickets(results);
      } catch (err) {
        console.error('Error fetching winning tickets:', err);
        setError('Could not fetch winning tickets.');
      } finally {
        setLoadingTickets(false);
      }
    };

    fetchWinningTickets();
  }, []);

  // 2) Handle toggling the dropdown for a ticket
  const handleToggle = async (ticketId, meciuriArray) => {
    if (expandedTicket === ticketId) {
      // If already expanded, collapse it
      setExpandedTicket(null);
      return;
    }

    // If not expanded, we open it. Check if we already have game data for this ticket
    if (!gamesMap[ticketId]) {
      // Need to fetch all games for meciuri
      try {
        const gameDocs = await fetchGamesForTicket(meciuriArray);
        setGamesMap((prev) => ({ ...prev, [ticketId]: gameDocs }));
      } catch (err) {
        console.error('Error fetching games for this ticket:', err);
      }
    }

    setExpandedTicket(ticketId);
  };

  // 3) Function to fetch games for a single ticket
  const fetchGamesForTicket = async (meciuriArray) => {
    // Each item might look like "meciuri/ABC123" or just "ABC123"
    // We'll handle both cases by removing "meciuri/" if present
    const fetchGameById = async (gamePath) => {
      const pureId = gamePath.replace('meciuri/', '');
      const docRef = doc(db, 'games', pureId);
      const snap = await getDoc(docRef);

      if (!snap.exists()) {
        throw new Error(`Game doc not found for ID ${pureId}`);
      }
      return { id: snap.id, ...snap.data() };
    };

    const promises = meciuriArray.map((gPath) => fetchGameById(gPath));
    const results = await Promise.all(promises);
    return results;
  };

  // For formatting date
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    return new Date(timestamp).toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-[#34495e] p-5">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-5 left-5 px-4 py-2 bg-[#2ecc71] rounded flex items-center space-x-2"
      >
       <FaArrowLeft />
      <span className="text-white">{t('btn.back')}</span>
      </button>

      <h1 className="text-xl text-white font-bold text-center mt-16 mb-8">{t('txt.winningTickets')}</h1>

      {loadingTickets && <p className="text-center text-gray-300">{t('txt.winningTicketsLoading')}</p>}
      {error && <p className="text-red-500 text-center">{error}</p>}

      {!loadingTickets && !error && tickets.length === 0 && (
        <p className="text-center text-gray-300">{t('txt.winningTicketsNotFound')}</p>
      )}

      {/* Ticket list */}
      <div className="max-w-[700px] mx-auto space-y-4">
        {tickets.map((ticket) => {
          const isExpanded = expandedTicket === ticket.id;
          const gamesForTicket = gamesMap[ticket.id] || [];

          return (
            <div
              key={ticket.id}
              className="rounded-xl overflow-hidden shadow-lg border border-white/10 bg-gradient-to-br from-emerald-500 to-emerald-800"
            >
              {/* Ticket overview */}
              <div className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-11 h-11 rounded-full bg-yellow-400/20 flex items-center justify-center">
                    <FaTrophy className="text-yellow-300 text-lg" />
                  </div>
                  <div className="min-w-0">
                    <span className="inline-block bg-yellow-400 text-emerald-900 text-[10px] font-extrabold px-2 py-0.5 rounded-full uppercase tracking-wide">
                      {t('txt.winningTicketsBadge')}
                    </span>
                    <p className="text-white text-2xl font-extrabold leading-tight mt-1">
                      {ticket.totalOdd.toFixed(2)}
                    </p>
                    <p className="text-emerald-100 text-xs">{t('txt.totalOdds')}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-white text-sm font-semibold">
                    {ticket.meciuri.length} {t('txt.winningTicketsMatches')}
                  </p>
                  <p className="text-emerald-100 text-xs">
                    {t('txt.created')}: {formatDate(ticket.dateCreated)}
                  </p>
                </div>
              </div>

              <button
                onClick={() => handleToggle(ticket.id, ticket.meciuri)}
                className="w-full flex items-center justify-center gap-2 bg-black/20 hover:bg-black/30 text-white text-sm font-semibold py-2.5 transition"
              >
                <span>{isExpanded ? t('txt.winningTicketsHideGames') : t('txt.winningTicketsShowGames')}</span>
                <FaChevronDown className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
              </button>

              {/* If expanded, show game details */}
              {isExpanded && (
                <div className="bg-white/95 p-3 space-y-2">
                  {gamesForTicket.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">{t('txt.pleaseWait')}</p>
                  ) : (
                    gamesForTicket.map((gm) => (
                      <GameCard key={gm.id} game={gm} />
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Footer />
    </div>
  );
};

// A small helper component for rendering game details
function GameCard({ game }) {
  return (
    <div className="flex items-start justify-between gap-3 bg-gray-50 rounded-lg px-3 py-2 border border-gray-100">
      <div className="min-w-0">
        <p className="text-sm font-bold text-gray-800 truncate">
          {game.team1} vs {game.team2}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {game.day} · {game.hour}
        </p>
        <p className="text-xs text-gray-600 truncate">
          {game.marketName} ({game.market})
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-sm font-bold text-emerald-600">{game.odd}</span>
        <FaCheckCircle className="text-emerald-500" />
      </div>
    </div>
  );
}

export default WinningTicketsPage;
